/**
 * One still frame out of a local video, taken safely.
 *
 * Why this exists: four screens each rendered a bare
 * `<video preload="metadata" src={url + '#t=0.01'}>` and trusted the browser to
 * paint the first frame. Chrome does. **iOS does not** — with `preload` short of
 * `auto` it paints nothing at all, and media fragments on a blob URL are not
 * honoured either. On Nation's phone (2026-08-04) that meant every video tile in
 * the editor, on the send screen and in the drafts sheet was an empty outline
 * while the photo beside it looked fine.
 *
 * So we stop asking and take the frame ourselves: load, seek a hair past zero,
 * draw one frame to a canvas, and hand back a data URL every browser paints the
 * same way.
 *
 * Lifted out of `atoms/VideoThumb.tsx` in FEAT-038 wave 3, when the pick path
 * needed the same extraction to store a poster: this is machinery, not a
 * component, and both callers must share the ONE lane below.
 */

/**
 * src → data URL. Object URLs are stable per capture, so this hits often.
 * Capped and FIFO-evicted: a long session picks and sends a lot of clips, and an
 * unbounded map of JPEGs is a slow leak nobody would ever look for.
 */
const frames = new Map<string, string>();
const FRAME_CACHE_MAX = 60;

/**
 * One extraction at a time, across every tile on screen.
 *
 * A batch renders up to eight tiles at once, and each extraction holds a whole
 * decoded video (`preload="auto"`) while it seeks. Eight of those in parallel is
 * precisely the memory spike that put the app in a crash loop on 2026-08-04 —
 * and a thumbnail is never worth that. They queue; the first one paints in
 * milliseconds and the rest follow.
 */
let lane: Promise<unknown> = Promise.resolve();

function inLane<T>(work: () => Promise<T>): Promise<T> {
  const run = lane.then(work, work);
  lane = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

/** Small enough to be cheap, big enough for a 2x tile. */
const THUMB_MAX = 192;

/** Not 0: frame zero of a just-encoded clip is sometimes not yet decodable. */
const SEEK_TO = 0.1;

/** Already taken, if it has been — the tiles read this synchronously so a
 * re-render never flashes an empty box. */
export function cachedFrame(src: string): string | undefined {
  return frames.get(src);
}

export function extractVideoFrame(src: string): Promise<string | null> {
  const cached = frames.get(src);
  if (cached) return Promise.resolve(cached);
  // Re-check inside the lane: eight tiles of the same clip would otherwise each
  // queue their own decode of a frame the first one already took.
  return inLane(() =>
    frames.get(src) ? Promise.resolve(frames.get(src)!) : decodeFrame(src)
  );
}

function decodeFrame(src: string): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    const video = document.createElement('video');
    let settled = false;
    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      if (value) {
        if (frames.size >= FRAME_CACHE_MAX) {
          const oldest = frames.keys().next().value;
          if (oldest !== undefined) frames.delete(oldest);
        }
        frames.set(src, value);
      }
      resolve(value);
    };
    // Bounded like every other decode in this app: a promise that never
    // settles is a tile that spins forever.
    const timer = setTimeout(() => finish(null), 8000);

    video.muted = true;
    video.playsInline = true;
    // `auto` is the point — `metadata` is exactly what iOS refuses to paint.
    video.preload = 'auto';
    video.onerror = () => finish(null);
    video.onloadeddata = () => {
      // A clip shorter than the seek point still has a frame at 0.
      video.currentTime = Math.min(SEEK_TO, Math.max(0, (video.duration || 0) - 0.01));
    };
    video.onseeked = () => {
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return finish(null);
        const scale = Math.min(1, THUMB_MAX / Math.max(w, h));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(w * scale));
        canvas.height = Math.max(1, Math.round(h * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) return finish(null);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const url = canvas.toDataURL('image/jpeg', 0.7);
        canvas.width = 0;
        canvas.height = 0;
        finish(url);
      } catch {
        // A cross-origin or otherwise tainted canvas — not our media, but
        // never a crash.
        finish(null);
      }
    };
    video.src = src;
  });
}
